import { XClose } from '@untitledui/icons'
import { Button as AriaButton, UNSTABLE_Toast as AriaToast, Text, UNSTABLE_ToastContent as ToastContent, UNSTABLE_ToastQueue as ToastQueue, UNSTABLE_ToastRegion as ToastRegion } from 'react-aria-components'
import { cx } from '@/utils/cx'

/**
 * One toast primitive for every write that otherwise changes nothing visible (audit P2-15): publish,
 * reassign (with Undo for 10 s, PRD-017 §3.4), due date, archive, density, copy codes. Built on React
 * Aria's toast (queue + region with the WAI-ARIA live-region and focus behavior), styled after the
 * Untitled UI notification: one line, optional action, close. Prior art: react-aria `ToastQueue`,
 * Sonner-style bottom-right stack, Gmail's "Undo" pattern for the reversible write.
 *
 * UX review 2026-09-21 note 43 (ui-standards C19): an error toast wraps to three lines with the full text on hover
 * and focus (a one-line `truncate` cut "A change note needs at least three words, so the next r…"), and the same
 * (tone, text) within a few seconds is one toast, not two (the definition editor fired one error from two
 * subscribers). Field-level validation belongs on the field, not here; the toast is for what no field can carry.
 */
export interface ToastContentT {
  text: string
  tone?: 'neutral' | 'success' | 'error'
  action?: { label: string, onAction: () => void }
}

export const toastQueue = new ToastQueue<ToastContentT>({ maxVisibleToasts: 3 })

const DEFAULT_TIMEOUT = 5_000
export const UNDO_TIMEOUT = 10_000
/** A second toast with the same tone and text inside this window is the same toast. */
export const DEDUPE_WINDOW_MS = 4_000
const recent = new Map<string, { key: string, at: number }>()

/** Pure: is `(tone, text)` a repeat of one shown within the window? Exported for the test; `toast()` calls it. */
export function dedupeKey(tone: ToastContentT['tone'], text: string, now: number, window = DEDUPE_WINDOW_MS): string | null {
  const id = `${tone ?? 'neutral'}\u0000${text}`
  const seen = recent.get(id)
  if (seen && now - seen.at < window)
    return seen.key
  for (const [k, v] of recent) {
    if (now - v.at >= window)
      recent.delete(k)
  }
  return null
}

function remember(tone: ToastContentT['tone'], text: string, key: string, now: number): void {
  recent.set(`${tone ?? 'neutral'}\u0000${text}`, { key, at: now })
}

/** Show a toast. Returns the key, so a caller can close it early (e.g. the Undo was taken). */
export function toast(text: string, opts: { tone?: ToastContentT['tone'], action?: ToastContentT['action'], timeout?: number } = {}): string {
  const now = Date.now()
  // An Undo toast is never collapsed onto an earlier one: each carries its own handle.
  const repeat = opts.action ? null : dedupeKey(opts.tone, text, now)
  if (repeat !== null && toastQueue.visibleToasts.some(v => v.key === repeat))
    return repeat
  const content: ToastContentT = { text }
  if (opts.tone)
    content.tone = opts.tone
  if (opts.action)
    content.action = opts.action
  const key = toastQueue.add(content, { timeout: opts.timeout ?? (opts.action ? UNDO_TIMEOUT : DEFAULT_TIMEOUT) })
  if (!opts.action)
    remember(opts.tone, text, key, now)
  return key
}

export function Toaster() {
  return (
    <ToastRegion queue={toastQueue} className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 outline-none" data-testid="toast-region">
      {({ toast: t }) => (
        <AriaToast
          toast={t}
          className={cx(
            'flex min-w-72 max-w-md items-start gap-3 rounded-lg border px-3 py-2 text-sm shadow-lg outline-none focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]',
            t.content.tone === 'error' ? 'border-error bg-error-secondary text-error-primary' : t.content.tone === 'success' ? 'border-success bg-success-secondary text-success-primary' : 'border-secondary bg-primary text-primary',
          )}
          data-testid="toast"
          data-tone={t.content.tone ?? 'neutral'}
        >
          <ToastContent className="min-w-0 flex-1">
            {/* Errors wrap (three lines, then the title attribute carries the rest); the quiet confirmations stay one line. */}
            <Text slot="title" className={cx('block', t.content.tone === 'error' ? 'line-clamp-3 break-words' : 'truncate')} title={t.content.text}>{t.content.text}</Text>
          </ToastContent>
          {t.content.action
            ? (
                <AriaButton
                  onPress={() => {
                    t.content.action!.onAction()
                    toastQueue.close(t.key)
                  }}
                  className="shrink-0 rounded-md px-2 py-1 text-sm font-medium text-brand-secondary hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-focus-ring"
                  data-testid="toast-action"
                >
                  {t.content.action.label}
                </AriaButton>
              )
            : null}
          <AriaButton slot="close" aria-label="Dismiss" className="flex size-6 shrink-0 items-center justify-center rounded-md text-quaternary hover:bg-primary_hover hover:text-primary focus-visible:outline-2 focus-visible:outline-focus-ring">
            <XClose className="size-4" />
          </AriaButton>
        </AriaToast>
      )}
    </ToastRegion>
  )
}
